import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Radio, Save, Send, AlertTriangle, Bell, Hash, ShieldAlert } from "lucide-react";
import { DEFCON_COLORS, DEFCON_ORDER } from "@/lib/defcon";

// Founder/Admin control room for the Crashout "Freshy Defcon" tracker.
export default function DefconAdmin() {
  const [d, setD] = useState(null);
  const [pending, setPending] = useState(null); // level selected but not applied
  const [note, setNote] = useState("");
  const [execDirective, setExecDirective] = useState("");   // required — Executive Managers
  const [staffDirective, setStaffDirective] = useState(""); // required — Admins & Moderators
  const [hostDirective, setHostDirective] = useState("");   // optional — Hosts
  const [airTime, setAirTime] = useState("");
  const [streamUrl, setStreamUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [cfg, setCfg] = useState(null); // editable copy of name/description/levels/audience/channels

  const load = async () => {
    try {
      const { data } = await api.get("/defcon/admin");
      setD(data);
      setAirTime(data.nuclear_air_time || "");
      setStreamUrl(data.nuclear_stream_url || "");
      setCfg({
        name: data.name || "",
        description: data.description || "",
        notify_audience: data.notify_audience || "followers",
        announce_channel_id: data.announce_channel_id || "",
        exec_channel_id: data.exec_channel_id || "",
        staff_channel_id: data.staff_channel_id || "",
        host_channel_id: data.host_channel_id || "",
        levels: JSON.parse(JSON.stringify(data.levels || {})),
      });
      setPending(null);
      setExecDirective("");
      setStaffDirective("");
      setHostDirective("");
    } catch (e) {
      toast.error("Failed to load DEFCON config");
    }
  };
  useEffect(() => { load(); }, []);

  const applyLevel = async () => {
    if (pending == null || pending === d.current_level) return;
    const nuclear = pending === 1;
    if (!execDirective || !staffDirective) {
      toast.error("Pick an Executive and an Admin/Mod directive before changing the level");
      return;
    }
    if (nuclear && (!airTime || !streamUrl.startsWith("http"))) {
      toast.error("DEFCON 1 needs an air time and a valid stream link (https://…)");
      return;
    }
    setApplying(true);
    try {
      const payload = {
        level: pending, note: note.trim(),
        exec_directive: execDirective, staff_directive: staffDirective,
        host_directive: hostDirective || null,
      };
      if (nuclear) { payload.air_time = airTime; payload.stream_url = streamUrl.trim(); }
      const { data } = await api.put("/defcon/level", payload);
      toast.success(data.notified ? `DEFCON ${pending} set — members + staff notified` : `DEFCON ${pending} set`);
      setNote("");
      setExecDirective("");
      setStaffDirective("");
      setHostDirective("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to set level");
    } finally {
      setApplying(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put("/defcon/config", {
        name: cfg.name, description: cfg.description,
        notify_audience: cfg.notify_audience, announce_channel_id: cfg.announce_channel_id,
        exec_channel_id: cfg.exec_channel_id, staff_channel_id: cfg.staff_channel_id,
        host_channel_id: cfg.host_channel_id,
        levels: cfg.levels,
      });
      toast.success("DEFCON configuration saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const setLevelField = (lvl, field, value) => {
    setCfg((c) => ({ ...c, levels: { ...c.levels, [lvl]: { ...(c.levels[lvl] || {}), [field]: value } } }));
  };

  if (!d || !cfg) return <div className="text-[var(--ff-text-dim)] p-4">Loading DEFCON…</div>;

  const cur = d.current_level;
  const curColor = DEFCON_COLORS[cur];
  const levelChanging = pending != null && pending !== cur;

  return (
    <div className="space-y-8" data-testid="defcon-admin">
      <div>
        <div className="label-eyebrow text-[var(--ff-red)] mb-2">▮ ADMIN / {(d.name || "Freshy Defcon").toUpperCase()}</div>
        <h2 className="font-display text-3xl mb-2 flex items-center gap-3">
          <Radio size={24} style={{ color: curColor }} /> {d.name} <span className="text-[var(--ff-text-dim)] text-xl">· {d.show_title}</span>
        </h2>
        <p className="text-[var(--ff-text-dim)] text-sm max-w-2xl">
          Set the live conflict status for <span className="text-white">{d.show_title}</span>. Changing the level DMs members
          {cfg.notify_audience === "all" ? " (everyone with Discord notifications on)" : " who follow the show or its hosts"}
          {cfg.announce_channel_id ? " and posts to the announcement channel" : ""}.
        </p>
      </div>

      {/* Current + level selector */}
      <div className="bg-[var(--ff-surface)] p-6" data-testid="defcon-set-card">
        <div className="flex items-center gap-4 mb-5">
          <div className="font-display leading-none" style={{ fontSize: "3.5rem", color: curColor }} data-testid="defcon-admin-current">{cur}</div>
          <div>
            <div className="label-eyebrow text-[var(--ff-text-dim)]">Current Level</div>
            <div className="font-display text-2xl">{(d.levels[String(cur)] || {}).label}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-4">
          {DEFCON_ORDER.map((lvl) => {
            const c = DEFCON_COLORS[lvl];
            const selected = (pending ?? cur) === lvl;
            return (
              <button
                key={lvl}
                onClick={() => setPending(lvl)}
                data-testid={`defcon-select-${lvl}`}
                className="p-3 border text-left transition-all"
                style={{ borderColor: selected ? c : "var(--ff-border)", background: selected ? `${c}22` : "transparent" }}
              >
                <div className="font-display text-2xl" style={{ color: c }}>{lvl}</div>
                <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.1em] text-[var(--ff-text-dim)] leading-tight mt-1">
                  {(d.levels[String(lvl)] || {}).label}
                </div>
              </button>
            );
          })}
        </div>

        {(pending ?? cur) === 1 && (
          <div className="mb-3 grid sm:grid-cols-2 gap-3 border border-[var(--ff-red)]/50 bg-[var(--ff-red)]/8 p-3" data-testid="defcon-nuclear-fields">
            <div>
              <label className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-red)] block mb-1">Air time today (ET) *</label>
              <input type="time" value={airTime} onChange={(e) => setAirTime(e.target.value)} className="w-full px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)]" data-testid="defcon-airtime-input" />
            </div>
            <div>
              <label className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-red)] block mb-1">Stream link *</label>
              <input type="url" value={streamUrl} onChange={(e) => setStreamUrl(e.target.value)} placeholder="https://…" className="w-full px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)]" data-testid="defcon-stream-input" />
            </div>
            <p className="sm:col-span-2 font-mono-ui text-[0.6rem] uppercase tracking-[0.12em] text-[var(--ff-text-dim)]">DEFCON 1 goes live <b>today</b> at this time. Both fields are required — fans get a countdown, a popup and a Watch-Live link.</p>
          </div>
        )}

        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={200}
          placeholder="Optional note to include in the announcement (e.g. what just happened)…"
          className="w-full px-3 py-2 text-sm mb-3 bg-[var(--ff-bg)] border border-[var(--ff-border)]"
          data-testid="defcon-note-input"
        />

        {levelChanging && (
          <div className="mb-3 border border-[var(--ff-red)]/50 bg-[var(--ff-red)]/8 p-3 space-y-3" data-testid="defcon-directive-field">
            <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-red)] flex items-center gap-1">
              <ShieldAlert size={11} /> Staff directives
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-white block mb-1">Executive Managers *</label>
                <select
                  value={execDirective}
                  onChange={(e) => setExecDirective(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)]"
                  data-testid="defcon-exec-directive-select"
                >
                  <option value="">Select a directive…</option>
                  {(d.directives || []).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-white block mb-1">Admins &amp; Moderators *</label>
                <select
                  value={staffDirective}
                  onChange={(e) => setStaffDirective(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)]"
                  data-testid="defcon-staff-directive-select"
                >
                  <option value="">Select a directive…</option>
                  {(d.directives || []).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-white block mb-1">Hosts (optional)</label>
                <select
                  value={hostDirective}
                  onChange={(e) => setHostDirective(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)]"
                  data-testid="defcon-host-directive-select"
                >
                  <option value="">No host directive — don't DM hosts</option>
                  {(d.host_directives || []).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="font-mono-ui text-[0.6rem] uppercase tracking-[0.12em] text-[var(--ff-text-dim)]">
              Executive &amp; Admin/Mod directives are required. Each group gets a private DM + a post in its Discord channel (if set below). Hosts only get pinged when you pick a host directive. None of this is shown to fans.
            </p>
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={applyLevel}
            disabled={applying || pending == null || pending === cur || !execDirective || !staffDirective || (pending === 1 && (!airTime || !streamUrl.startsWith("http")))}
            data-testid="defcon-apply-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 font-mono-ui text-xs uppercase tracking-[0.18em] bg-[var(--ff-red)] text-white hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send size={13} /> {applying ? "Sending…" : pending != null && pending !== cur ? `Set DEFCON ${pending} & Notify` : "Select a new level"}
          </button>
          {pending != null && pending <= 2 && pending !== cur && (
            <span className="inline-flex items-center gap-1 font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-red)]">
              <AlertTriangle size={12} /> High-alert broadcast
            </span>
          )}
        </div>
      </div>

      {/* Customize copy */}
      <div className="bg-[var(--ff-surface)] p-6 space-y-4" data-testid="defcon-config-card">
        <div className="label-eyebrow">Customize DEFCON</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] block mb-1">Tracker name</label>
            <input value={cfg.name} onChange={(e) => setCfg({ ...cfg, name: e.target.value })} maxLength={80} className="w-full px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)]" data-testid="defcon-name-input" />
          </div>
          <div>
            <label className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] block mb-1 flex items-center gap-1"><Bell size={11} /> Notify audience</label>
            <select value={cfg.notify_audience} onChange={(e) => setCfg({ ...cfg, notify_audience: e.target.value })} className="w-full px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)]" data-testid="defcon-audience-select">
              <option value="followers">Followers of {d.show_title} (+ its hosts)</option>
              <option value="all">Everyone with Discord notifications on</option>
            </select>
          </div>
        </div>
        <div>
          <label className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] block mb-1">Description</label>
          <textarea value={cfg.description} onChange={(e) => setCfg({ ...cfg, description: e.target.value })} rows={2} maxLength={600} className="w-full px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)] resize-y" data-testid="defcon-description-input" />
        </div>
        <div>
          <label className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] block mb-1 flex items-center gap-1"><Hash size={11} /> Announcement channel ID (optional)</label>
          <input value={cfg.announce_channel_id} onChange={(e) => setCfg({ ...cfg, announce_channel_id: e.target.value })} maxLength={40} placeholder="Discord channel ID — also post changes here" className="w-full px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)]" data-testid="defcon-channel-input" />
        </div>

        <div className="space-y-3 pt-2 border-t border-[var(--ff-border)]">
          <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-red)] flex items-center gap-1"><ShieldAlert size={11} /> Directive channels (optional)</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] block mb-1">Executive Managers</label>
              <input value={cfg.exec_channel_id} onChange={(e) => setCfg({ ...cfg, exec_channel_id: e.target.value })} maxLength={40} placeholder="Channel ID" className="w-full px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)]" data-testid="defcon-exec-channel-input" />
            </div>
            <div>
              <label className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] block mb-1">Admins &amp; Moderators</label>
              <input value={cfg.staff_channel_id} onChange={(e) => setCfg({ ...cfg, staff_channel_id: e.target.value })} maxLength={40} placeholder="Channel ID" className="w-full px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)]" data-testid="defcon-staff-channel-input" />
            </div>
            <div>
              <label className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] block mb-1">Hosts</label>
              <input value={cfg.host_channel_id} onChange={(e) => setCfg({ ...cfg, host_channel_id: e.target.value })} maxLength={40} placeholder="Channel ID" className="w-full px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)]" data-testid="defcon-host-channel-input" />
            </div>
          </div>
          <p className="font-mono-ui text-[0.6rem] uppercase tracking-[0.12em] text-[var(--ff-text-dim)]">Each group's DEFCON directive is also posted to its channel here, on top of the private DMs.</p>
        </div>

        <div className="space-y-2 pt-2 border-t border-[var(--ff-border)]">
          <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">Levels</div>
          {DEFCON_ORDER.map((lvl) => {
            const c = DEFCON_COLORS[lvl];
            const l = cfg.levels[String(lvl)] || {};
            return (
              <div key={lvl} className="flex gap-2 items-start" data-testid={`defcon-level-edit-${lvl}`}>
                <div className="font-display text-2xl w-8 text-center shrink-0 pt-1" style={{ color: c }}>{lvl}</div>
                <div className="flex-1 grid sm:grid-cols-3 gap-2">
                  <input value={l.label || ""} onChange={(e) => setLevelField(String(lvl), "label", e.target.value)} maxLength={80} placeholder="Label" className="px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)]" data-testid={`defcon-level-label-${lvl}`} />
                  <input value={l.description || ""} onChange={(e) => setLevelField(String(lvl), "description", e.target.value)} maxLength={400} placeholder="Description" className="sm:col-span-2 px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)]" data-testid={`defcon-level-desc-${lvl}`} />
                </div>
              </div>
            );
          })}
        </div>

        <button
          onClick={saveConfig}
          disabled={saving}
          data-testid="defcon-save-config-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 font-mono-ui text-xs uppercase tracking-[0.18em] border border-[var(--ff-border)] hover:border-[var(--ff-red)] hover:text-[var(--ff-red)] transition-colors disabled:opacity-50"
        >
          <Save size={13} /> {saving ? "Saving…" : "Save Configuration"}
        </button>
        <p className="font-mono-ui text-[0.6rem] uppercase tracking-[0.12em] text-[var(--ff-text-dim)]">Saving copy does not notify members — only changing the level does.</p>
      </div>

      {/* Recent changes */}
      {d.recent_changes && d.recent_changes.length > 0 && (
        <div className="bg-[var(--ff-surface)] p-6" data-testid="defcon-log">
          <div className="label-eyebrow mb-3">Recent changes</div>
          <div className="space-y-2">
            {d.recent_changes.map((r) => (
              <div key={r.id} className="flex items-center justify-between font-mono-ui text-xs border-b border-[var(--ff-border)] pb-2">
                <span className="flex items-center gap-2">
                  <span className="font-display text-lg" style={{ color: DEFCON_COLORS[r.level] }}>{r.level}</span>
                  <span className="text-white">{r.label}</span>
                  <span className="text-[var(--ff-text-dim)]">· by {r.actor}</span>
                </span>
                <span className="text-[var(--ff-text-dim)]">DM {r.sent}/{r.recipients}{r.channel_posted ? " · #ch" : ""} · {new Date(r.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
